import { useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useShop } from "@/context/ShopContext";
import api, { inr } from "@/lib/api";
import ProductForm from "@/components/admin/ProductForm";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const ProductsTab = () => {
  const { products, refreshProducts } = useShop();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const save = async (payload) => {
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added to the shelf");
      }
      setFormOpen(false);
      setEditing(null);
      refreshProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save product");
    }
  };

  const quickUpdate = async (p, changes) => {
    try {
      const { id, created_at, ...body } = p;
      await api.put(`/products/${p.id}`, { ...body, ...changes });
      refreshProducts();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/products/${deleting.id}`);
      toast.success(`${deleting.name} removed`);
      setDeleting(null);
      refreshProducts();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-products-tab">
      <div className="mb-5 flex items-center justify-between">
        <p className="text-sm text-clay/60">{products.length} products on the shelf</p>
        <button
          data-testid="admin-create-product-button"
          onClick={() => { setEditing(null); setFormOpen(true); }}
          className="flex items-center gap-2 rounded-full bg-terra px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-terra-dark active:scale-[0.98]"
        >
          <Plus className="h-4 w-4" /> Add product
        </button>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-linen bg-white" data-testid="admin-products-data-table">
        <table className="w-full min-w-[820px] text-left text-sm">
          <thead>
            <tr className="border-b border-linen font-mono text-[10px] uppercase tracking-[0.18em] text-clay/60">
              <th className="px-5 py-3.5">Product</th>
              <th className="px-5 py-3.5">Category</th>
              <th className="px-5 py-3.5">Price ₹</th>
              <th className="px-5 py-3.5">Stock</th>
              <th className="px-5 py-3.5">Featured</th>
              <th className="px-5 py-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-linen/70">
            {products.map((p) => (
              <tr key={p.id} className="transition-colors hover:bg-sand/40">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <img src={p.image} alt="" className="h-11 w-11 rounded-lg object-cover" />
                    <div>
                      <p className="font-medium text-espresso">{p.name}</p>
                      <p className="font-mono text-[10px] text-clay/50">{p.id}</p>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3 text-clay/70">{p.category}</td>
                <td className="px-5 py-3">
                  <input
                    type="number"
                    defaultValue={p.price}
                    key={`price-${p.id}-${p.price}`}
                    onBlur={(e) => Number(e.target.value) !== p.price && quickUpdate(p, { price: Number(e.target.value) })}
                    className="w-24 rounded-lg border border-linen bg-cream/50 px-2.5 py-1.5 font-mono text-sm outline-none focus:border-terra"
                    data-testid={`product-price-input-${p.id}`}
                  />
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      defaultValue={p.stock}
                      key={`stock-${p.id}-${p.stock}`}
                      onBlur={(e) => Number(e.target.value) !== p.stock && quickUpdate(p, { stock: Number(e.target.value) })}
                      className="w-20 rounded-lg border border-linen bg-cream/50 px-2.5 py-1.5 font-mono text-sm outline-none focus:border-terra"
                      data-testid={`product-stock-input-${p.id}`}
                    />
                    {p.stock === 0 ? (
                      <span className="rounded-full bg-destructive/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-destructive">Out</span>
                    ) : p.stock < 6 ? (
                      <span className="rounded-full bg-ochre/15 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-ochre">Low</span>
                    ) : null}
                  </div>
                </td>
                <td className="px-5 py-3">
                  <Switch checked={p.featured} onCheckedChange={(v) => quickUpdate(p, { featured: v })} data-testid={`product-featured-switch-${p.id}`} />
                </td>
                <td className="px-5 py-3">
                  <div className="flex justify-end gap-1.5">
                    <button onClick={() => { setEditing(p); setFormOpen(true); }} className="rounded-lg p-2 text-clay/60 transition-colors hover:bg-sand hover:text-espresso" aria-label={`Edit ${p.name}`} data-testid={`product-edit-${p.id}`}>
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => setDeleting(p)} className="rounded-lg p-2 text-clay/60 transition-colors hover:bg-destructive/10 hover:text-destructive" aria-label={`Delete ${p.name}`} data-testid={`product-delete-${p.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-clay/40">Tip: click a price or stock number, type, then click away to save</p>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-cream sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-espresso">{editing ? "Edit product" : "New product"}</DialogTitle>
          </DialogHeader>
          <ProductForm initial={editing} onSave={save} onCancel={() => setFormOpen(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <DialogContent className="bg-cream sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-espresso">Remove {deleting?.name}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-clay/70">This permanently removes the product from your storefront.</p>
          <div className="mt-5 flex justify-end gap-3">
            <button onClick={() => setDeleting(null)} className="rounded-full border border-linen px-5 py-2.5 text-sm font-semibold text-clay hover:border-clay">Keep it</button>
            <button onClick={remove} data-testid="product-delete-confirm" className="rounded-full bg-destructive px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90">Remove</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsTab;
